import { httpClient } from './http-client';

/** The durable memory reused by every analysis. Every field is optional:
 * a first analysis runs before anyone has filled anything in. */
export interface FinancialProfile {
  household_id?: string;
  birth_year?: number | null;
  retirement_age?: number | null;
  annual_spend?: string | null;
  risk_tolerance?: string;
  province?: string;
  dependants?: number | null;
  notes?: string;
  updated_at?: string;
}

export interface AnalysisAccount {
  name: string;
  type: string;
  currency: string;
  balance: string;
  balance_base: string;
}

export interface ConcentrationLine {
  symbol: string;
  value: string;
  percent: string;
}

export interface MonthAmount {
  month: string;
  amount: string;
}

export interface AnalysisSnapshot {
  taken_at: string;
  base_currency: string;
  net_worth: string;
  accounts: AnalysisAccount[];
  positions: unknown[];
  concentration: ConcentrationLine[];
  income_by_month: MonthAmount[];
  trailing_12m_income: string;
  age?: number;
  years_to_retirement?: number;
}

export interface AnalysisContext {
  snapshot_id: string;
  profile: FinancialProfile;
  snapshot: AnalysisSnapshot;
  brief: string;
  instructions: string;
}

/** A chart the model asked for. Specs rather than images, so the app draws
 * them in its own theme alongside every other chart. */
export interface ChartSpec {
  type: 'bar' | 'line' | 'pie' | string;
  title?: string;
  series: { name: string; points: { x: string | number; y: number }[] }[];
}

export interface Analysis {
  id: string;
  prompt: string;
  title?: string;
  source: 'mcp' | 'llm' | 'planner';
  model?: string;
  snapshot_id?: string | null;
  result_md: string;
  charts: ChartSpec[];
  created_at: string;
}

export const AnalysisService = {
  async getProfile(): Promise<FinancialProfile> {
    return httpClient.get<FinancialProfile>('/analysis/profile');
  },

  /** Merges: omitted fields keep their stored value, so teaching the profile
   * one new fact never blanks the others. */
  async saveProfile(p: FinancialProfile): Promise<FinancialProfile> {
    return httpClient.put<FinancialProfile>('/analysis/profile', p);
  },

  /** Takes and stores a fresh snapshot, and returns everything a model needs
   * in one round trip. */
  async getContext(): Promise<AnalysisContext> {
    return httpClient.get<AnalysisContext>('/analysis/context');
  },

  async list(): Promise<{ analyses: Analysis[]; count: number }> {
    return httpClient.get<{ analyses: Analysis[]; count: number }>('/analyses');
  },

  async get(id: string): Promise<{
    analysis: Analysis;
    snapshot?: AnalysisSnapshot;
    snapshot_taken_at?: string;
  }> {
    return httpClient.get(`/analyses/${id}`);
  },

  async create(a: Partial<Analysis>): Promise<Analysis> {
    return httpClient.post<Analysis>('/analyses', a);
  },

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/analyses/${id}`);
  },
};

/** A ready-made question, versioned with the code rather than seeded. */
export interface AnalysisPreset {
  key: string;
  name: string;
  description: string;
  prompt: string;
}

export interface SavedPrompt {
  id: string;
  name: string;
  prompt: string;
  schedule: 'none' | 'weekly' | 'monthly' | 'quarterly';
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_error?: string;
  enabled: boolean;
  created_at: string;
}

export const PromptService = {
  async list(): Promise<{
    presets: AnalysisPreset[];
    saved: SavedPrompt[];
    scheduling_available: boolean;
  }> {
    return httpClient.get('/analysis/prompts');
  },

  async save(p: {
    id?: string; name: string; prompt: string;
    schedule?: string; enabled?: boolean;
  }): Promise<SavedPrompt> {
    return httpClient.post<SavedPrompt>('/analysis/prompts', p);
  },

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/analysis/prompts/${id}`);
  },

  /** Runs server-side against the household's own LLM key. MCP runs never
   * come through here — they execute in the user's client. */
  async run(p: { name: string; prompt: string }): Promise<Analysis> {
    return httpClient.post<Analysis>('/analysis/prompts/run', p);
  },
};
